/** Public /screen form copy — EN, FR, ZH, SW, Amharic, ES, AR */

export type PublicLang = 'en' | 'fr' | 'zh' | 'sw' | 'am' | 'es' | 'ar';

export const PUBLIC_LANG_OPTIONS: { code: PublicLang; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'zh', label: '中文' },
  { code: 'sw', label: 'Kiswahili' },
  { code: 'am', label: 'አማርኛ' },
  { code: 'es', label: 'Español' },
  { code: 'ar', label: 'العربية' },
];

type Dict = Record<string, string>;

const en: Dict = {
  brand_eyebrow: 'Republic of Kenya · MoH',
  brand_title: 'Traveler Health Screening',
  brand_sub: 'Ebola entry form — no login. Temp at checkpoint.',
  language: 'Language',
  demographics: 'Demographics',
  travel: 'Travel Details',
  exposure: 'Travel History / Exposure',
  symptoms: 'Symptoms *',
  symptoms_hint: 'Select all that apply',
  full_name: 'Full name *',
  age: 'Age *',
  gender: 'Gender *',
  male: 'Male',
  female: 'Female',
  other: 'Other',
  nationality: 'Nationality *',
  passport: 'Passport/ID Number *',
  country_code: 'Country code *',
  country_code_hint: 'e.g. +254',
  phone: 'Phone number *',
  phone_hint: 'Local number without country code',
  departure: 'Country of departure *',
  country_name: 'Country name *',
  mode: 'Mode of transport *',
  air: 'Air',
  ship: 'Ship',
  road: 'Road',
  poe: 'Point of entry *',
  poe_loading: 'Loading entry points…',
  poe_need_mode: 'Select mode of transport first',
  poe_error: 'Could not load points of entry',
  flight: 'Flight number *',
  vessel: 'Vessel number *',
  vehicle: 'Vehicle number *',
  seat: 'Seat number *',
  visited_ug_drc: 'Visited Uganda or DR Congo in the last 21 days? *',
  yes: 'Yes',
  no: 'No',
  exp_contact: 'Contact with a confirmed or suspected Ebola case?',
  exp_healthcare: 'Healthcare or laboratory work in an affected area?',
  exp_funeral: 'Attended a funeral or body preparation?',
  exp_animals: 'Contact with bats, primates, or monkeys?',
  exp_bushmeat: 'Hunted, slaughtered, or handled bushmeat?',
  exp_forest: 'Exposure to forests or caves?',
  sym_headache: 'Headache',
  sym_muscle: 'Muscle or joint pain',
  sym_vomit: 'Vomiting or nausea',
  sym_diarrhoea: 'Diarrhoea',
  sym_abdominal: 'Abdominal pain',
  sym_bleeding: 'Bleeding or bruising',
  sym_rash: 'Haemorrhagic rash or petechiae',
  sym_eyes: 'Conjunctival injection (red eyes)',
  sym_throat: 'Sore throat',
  sym_swallow: 'Difficulty swallowing',
  sym_consciousness: 'Altered consciousness',
  sym_none: 'None of the above',
  submit: 'Submit & get pass',
  submitting: 'Submitting…',
  success: 'Screening submitted. Show this pass at the health checkpoint.',
  success_cleared: '✅ Your Ebola travel health screening is complete and you are cleared for travel.\n\nPlease expect daily follow-up messages for 21 days.',
  success_high_risk: 'Kindly proceed to the Port health desk and show the QR Code',
  qr_caption: 'Show this QR Code at the Port Health desk',
  download_pass: 'Download pass (PDF)',
  download_qr: 'Download QR code',
  downloading: 'Downloading…',
  err_download: 'Download failed. Please try again.',
  another: 'Submit another screening',
  err_phone: 'Country code and phone number are both required.',
  err_phone_valid: 'Enter a valid country code and phone number.',
  err_required: 'Please fill in all required fields marked with *.',
  err_symptoms: 'Please select at least one symptom, or “None of the above”.',
  err_save: 'Failed to submit screening. Please try again.',
  no_pass: 'No pass generated yet.',
};

const fr: Dict = {
  ...en,
  brand_eyebrow: 'République du Kenya · MoH',
  brand_title: 'Dépistage sanitaire des voyageurs',
  brand_sub: 'Formulaire Ebola — sans connexion. Température au point de contrôle.',
  language: 'Langue',
  demographics: 'Données démographiques',
  travel: 'Détails du voyage',
  exposure: 'Antécédents / Exposition',
  symptoms: 'Symptômes *',
  symptoms_hint: 'Cochez tout ce qui s’applique',
  full_name: 'Nom complet *',
  age: 'Âge *',
  gender: 'Genre *',
  male: 'Homme',
  female: 'Femme',
  other: 'Autre',
  nationality: 'Nationalité *',
  passport: 'Passeport / N° d’identité *',
  country_code: 'Indicatif pays *',
  country_code_hint: 'ex. +254',
  phone: 'Numéro de téléphone *',
  phone_hint: 'Numéro local sans indicatif',
  departure: 'Pays de départ *',
  country_name: 'Nom du pays *',
  mode: 'Mode de transport *',
  air: 'Air',
  ship: 'Mer',
  road: 'Route',
  poe: 'Point d’entrée *',
  poe_loading: 'Chargement des points d’entrée…',
  poe_need_mode: 'Sélectionnez d’abord le mode de transport',
  poe_error: 'Impossible de charger les points d’entrée',
  flight: 'N° de vol *',
  vessel: 'N° de navire *',
  vehicle: 'N° de véhicule *',
  seat: 'N° de siège *',
  visited_ug_drc: 'Avez-vous visité l’Ouganda ou la RDC au cours des 21 derniers jours ? *',
  yes: 'Oui',
  no: 'Non',
  exp_contact: 'Contact avec un cas d’Ebola confirmé ou suspect ?',
  exp_healthcare: 'Travail sanitaire ou de laboratoire dans une zone touchée ?',
  exp_funeral: 'Participation à des funérailles ou à une préparation du corps ?',
  exp_animals: 'Contact avec des chauves-souris, primates ou singes ?',
  exp_bushmeat: 'Chasse, abattage ou manipulation de viande de brousse ?',
  exp_forest: 'Exposition aux forêts ou aux grottes ?',
  sym_headache: 'Maux de tête',
  sym_muscle: 'Douleurs musculaires ou articulaires',
  sym_vomit: 'Vomissements ou nausées',
  sym_diarrhoea: 'Diarrhée',
  sym_abdominal: 'Douleurs abdominales',
  sym_bleeding: 'Saignements ou ecchymoses',
  sym_rash: 'Éruption hémorragique ou pétéchies',
  sym_eyes: 'Yeux rouges (injection conjonctivale)',
  sym_throat: 'Mal de gorge',
  sym_swallow: 'Difficulté à avaler',
  sym_consciousness: 'Altération de la conscience',
  sym_none: 'Aucun des symptômes ci-dessus',
  submit: 'Envoyer et obtenir le laissez-passer',
  submitting: 'Envoi…',
  success: 'Dépistage enregistré. Présentez ce laissez-passer au point de contrôle.',
  success_cleared: '✅ Votre dépistage sanitaire Ebola pour le voyage est terminé et vous êtes autorisé à voyager.\n\nVeuillez vous attendre à des messages de suivi quotidiens pendant 21 jours.',
  success_high_risk: 'Veuillez vous présenter au bureau de santé portuaire et montrer le QR Code',
  qr_caption: 'Montrez ce QR Code au bureau de santé portuaire',
  download_pass: 'Télécharger le laissez-passer (PDF)',
  download_qr: 'Télécharger le QR code',
  downloading: 'Téléchargement…',
  err_download: 'Échec du téléchargement. Réessayez.',
  another: 'Nouveau dépistage',
  err_phone: 'Indicatif et numéro de téléphone requis.',
  err_phone_valid: 'Indicatif et numéro valides requis.',
  err_required: 'Veuillez remplir tous les champs obligatoires (*).',
  err_symptoms: 'Sélectionnez au moins un symptôme, ou « Aucun des symptômes ci-dessus ».',
  err_save: 'Échec de l’envoi. Réessayez.',
  no_pass: 'Aucun laissez-passer généré.',
};

const zh: Dict = {
  ...en,
  brand_eyebrow: '肯尼亚共和国 · 卫生部',
  brand_title: '旅客健康筛查',
  brand_sub: '埃博拉入境表 — 无需登录。体温在检查点测量。',
  language: '语言',
  demographics: '基本信息',
  travel: '旅行信息',
  exposure: '旅行史 / 暴露史',
  symptoms: '症状 *',
  symptoms_hint: '请选择所有适用项',
  full_name: '全名 *',
  age: '年龄 *',
  gender: '性别 *',
  male: '男',
  female: '女',
  other: '其他',
  nationality: '国籍 *',
  passport: '护照/证件号 *',
  country_code: '国家区号 *',
  country_code_hint: '例如 +254',
  phone: '电话号码 *',
  phone_hint: '不含国家区号的本地号码',
  departure: '出发国家 *',
  country_name: '国家名称 *',
  mode: '交通方式 *',
  air: '航空',
  ship: '海运',
  road: '陆路',
  poe: '入境口岸 *',
  poe_loading: '正在加载口岸…',
  poe_need_mode: '请先选择交通方式',
  poe_error: '无法加载口岸列表',
  flight: '航班号 *',
  vessel: '船舶号 *',
  vehicle: '车辆号 *',
  seat: '座位号 *',
  visited_ug_drc: '过去21天内是否去过乌干达或刚果（金）？ *',
  yes: '是',
  no: '否',
  exp_contact: '是否接触确诊或疑似埃博拉病例？',
  exp_healthcare: '是否在疫区从事医疗或实验室工作？',
  exp_funeral: '是否参加葬礼或尸体处理？',
  exp_animals: '是否接触蝙蝠、灵长类或猴子？',
  exp_bushmeat: '是否狩猎、屠宰或处理野味？',
  exp_forest: '是否接触森林或洞穴？',
  sym_headache: '头痛',
  sym_muscle: '肌肉或关节疼痛',
  sym_vomit: '呕吐或恶心',
  sym_diarrhoea: '腹泻',
  sym_abdominal: '腹痛',
  sym_bleeding: '出血或瘀伤',
  sym_rash: '出血性皮疹或瘀点',
  sym_eyes: '结膜充血（红眼）',
  sym_throat: '咽痛',
  sym_swallow: '吞咽困难',
  sym_consciousness: '意识改变',
  sym_none: '以上皆无',
  submit: '提交并获取通行证',
  submitting: '提交中…',
  success: '筛查已提交。请在健康检查点出示此通行证。',
  success_cleared: '✅ 您的埃博拉旅行健康筛查已完成，您已获准旅行。\n\n请留意今后21天的每日随访信息。',
  success_high_risk: '请前往口岸卫生台并出示二维码',
  qr_caption: '请在口岸卫生台出示此二维码',
  download_pass: '下载通行证（PDF）',
  download_qr: '下载二维码',
  downloading: '下载中…',
  err_download: '下载失败，请重试。',
  another: '再次筛查',
  err_phone: '国家区号和电话号码均为必填。',
  err_phone_valid: '请输入有效的国家区号和电话号码。',
  err_required: '请填写所有标有 * 的必填项。',
  err_symptoms: '请至少选择一项症状，或选择“以上皆无”。',
  err_save: '提交失败，请重试。',
  no_pass: '尚未生成通行证。',
};

const sw: Dict = {
  ...en,
  brand_eyebrow: 'Jamhuri ya Kenya · MoH',
  brand_title: 'Uchunguzi wa Afya wa Msafiri',
  brand_sub: 'Fomu ya Ebola — hakuna kuingia. Joto hupimwa kituoni.',
  language: 'Lugha',
  demographics: 'Taarifa binafsi',
  travel: 'Maelezo ya safari',
  exposure: 'Historia / Mfiduo',
  symptoms: 'Dalili *',
  symptoms_hint: 'Chagua zote zinazotumika',
  full_name: 'Jina kamili *',
  age: 'Umri *',
  gender: 'Jinsia *',
  male: 'Mwanaume',
  female: 'Mwanamke',
  other: 'Nyingine',
  nationality: 'Uraia *',
  passport: 'Pasipoti / Nambari ya kitambulisho *',
  country_code: 'Msimbo wa nchi *',
  country_code_hint: 'mf. +254',
  phone: 'Nambari ya simu *',
  phone_hint: 'Nambari ya ndani bila msimbo wa nchi',
  departure: 'Nchi ya kuondoka *',
  country_name: 'Jina la nchi *',
  mode: 'Njia ya usafiri *',
  air: 'Anga',
  ship: 'Bahari',
  road: 'Barabara',
  poe: 'Kituo cha kuingia *',
  poe_loading: 'Inapakia vituo…',
  poe_need_mode: 'Chagua njia ya usafiri kwanza',
  poe_error: 'Imeshindwa kupakia vituo',
  flight: 'Nambari ya ndege *',
  vessel: 'Nambari ya meli *',
  vehicle: 'Nambari ya gari *',
  seat: 'Nambari ya kiti *',
  visited_ug_drc: 'Je, umetembelea Uganda au DRC siku 21 zilizopita? *',
  yes: 'Ndiyo',
  no: 'Hapana',
  exp_contact: 'Mawasiliano na kesi ya Ebola iliyothibitishwa au inayoshukiwa?',
  exp_healthcare: 'Kazi ya afya au maabara katika eneo lililoathiriwa?',
  exp_funeral: 'Kuhudhuria mazishi au kuandaa mwili?',
  exp_animals: 'Mawasiliano na popo, nyani au tumbili?',
  exp_bushmeat: 'Uwindaji, kuchinja au kushughulikia nyama pori?',
  exp_forest: 'Mfiduo wa misitu au mapango?',
  sym_headache: 'Maumivu ya kichwa',
  sym_muscle: 'Maumivu ya misuli au viungo',
  sym_vomit: 'Kutapika au kichefuchefu',
  sym_diarrhoea: 'Kuhara',
  sym_abdominal: 'Maumivu ya tumbo',
  sym_bleeding: 'Kutokwa damu au michubuko',
  sym_rash: 'Upele wa damu au petechia',
  sym_eyes: 'Macho mekundu',
  sym_throat: 'Koo kuuma',
  sym_swallow: 'Ugumu wa kumeza',
  sym_consciousness: 'Mabadiliko ya fahamu',
  sym_none: 'Hakuna kati ya hizi',
  submit: 'Wasilisha na pata pasi',
  submitting: 'Inawasilisha…',
  success: 'Uchunguzi umewasilishwa. Onyesha pasi hii kituoni.',
  success_cleared: '✅ Uchunguzi wako wa afya wa Ebola wa safari umekamilika na umeruhusiwa kusafiri.\n\nTafadhali tarajia ujumbe wa ufuatiliaji wa kila siku kwa siku 21.',
  success_high_risk: 'Tafadhali nenda kwenye dawati la afya la bandari na uonyeshe QR Code',
  qr_caption: 'Onyesha QR Code hii kwenye dawati la afya la bandari',
  download_pass: 'Pakua pasi (PDF)',
  download_qr: 'Pakua QR Code',
  downloading: 'Inapakua…',
  err_download: 'Imeshindwa kupakua. Jaribu tena.',
  another: 'Uchunguzi mwingine',
  err_phone: 'Msimbo wa nchi na nambari ya simu zinahitajika.',
  err_phone_valid: 'Weka msimbo na nambari sahihi.',
  err_required: 'Tafadhali jaza sehemu zote zinazohitajika (*).',
  err_symptoms: 'Chagua angalau dalili moja, au “Hakuna kati ya hizi”.',
  err_save: 'Imeshindwa kuwasilisha. Jaribu tena.',
  no_pass: 'Hakuna pasi bado.',
};

const am: Dict = {
  ...en,
  brand_eyebrow: 'የኬንያ ሪፐብሊክ · MoH',
  brand_title: 'የተጓዥ ጤና ምርመራ',
  brand_sub: 'የኢቦላ ቅጽ — መግባት አያስፈልግም። ሙቀት በፍተሻ ነጥብ ይለካል።',
  language: 'ቋንቋ',
  demographics: 'የግል መረጃ',
  travel: 'የጉዞ ዝርዝር',
  exposure: 'ታሪክ / ተጋላጭነት',
  symptoms: 'ምልክቶች *',
  symptoms_hint: 'ሁሉንም ተግባራዊ የሆኑትን ይምረጡ',
  full_name: 'ሙሉ ስም *',
  age: 'ዕድሜ *',
  gender: 'ፆታ *',
  male: 'ወንድ',
  female: 'ሴት',
  other: 'ሌላ',
  nationality: 'ዜግነት *',
  passport: 'ፓስፖርት / መታወቂያ ቁጥር *',
  country_code: 'የአገር ኮድ *',
  country_code_hint: 'ለምሳሌ +254',
  phone: 'ስልክ ቁጥር *',
  phone_hint: 'ያለ የአገር ኮድ የአካባቢ ቁጥር',
  departure: 'የመነሻ አገር *',
  country_name: 'የአገር ስም *',
  mode: 'የትራንስፖርት መንገድ *',
  air: 'አየር',
  ship: 'ባህር',
  road: 'መንገድ',
  poe: 'የመግቢያ ነጥብ *',
  poe_loading: 'ነጥቦች በመጫን ላይ…',
  poe_need_mode: 'መጀመሪያ የትራንስፖርት መንገድ ይምረጡ',
  poe_error: 'ነጥቦችን መጫን አልተቻለም',
  flight: 'የበረራ ቁጥር *',
  vessel: 'የመርከብ ቁጥር *',
  vehicle: 'የተሽከርካሪ ቁጥር *',
  seat: 'የመቀመጫ ቁጥር *',
  visited_ug_drc: 'ባለፉት 21 ቀናት ውስጥ ኡጋንዳ ወይም ኮንጎ ዲሞክራሲያዊ ሪፐብሊክ ጎብኝተዋል? *',
  yes: 'አዎ',
  no: 'አይ',
  exp_contact: 'ከተረጋገጠ ወይም ከተጠረጠረ የኢቦላ ጉዳይ ጋር ግንኙነት?',
  exp_healthcare: 'በተጎዳ አካባቢ የጤና ወይም ላቦራቶሪ ሥራ?',
  exp_funeral: 'በቀብር ወይም በአስከሬን ዝግጅት ተሳትፈዋል?',
  exp_animals: 'ከሌሊት ወፎች፣ ፕሪሜቶች ወይም ዝንጀሮዎች ጋር ግንኙነት?',
  exp_bushmeat: 'የዱር ሥጋ ማደን፣ መረጨት ወይም ማስተናገድ?',
  exp_forest: 'ለደን ወይም ለዋሻ መጋለጥ?',
  sym_headache: 'ራስ ምታት',
  sym_muscle: 'የጡንቻ ወይም የመገጣጠሚያ ህመም',
  sym_vomit: 'ማስመለስ ወይም ማቅለሽለሽ',
  sym_diarrhoea: 'ተቅማጥ',
  sym_abdominal: 'የሆድ ህመም',
  sym_bleeding: 'ደም መፍሰስ ወይም ሰማያዊ',
  sym_rash: 'የደም ሽፍታ',
  sym_eyes: 'ቀይ ዓይኖች',
  sym_throat: 'የጉሮሮ ህመም',
  sym_swallow: 'መዋጥ አስቸጋሪ',
  sym_consciousness: 'የንቃተ ህሊና ለውጥ',
  sym_none: 'ከላይ ያሉት አይደሉም',
  submit: 'አስገባ እና ፓስ አግኝ',
  submitting: 'በማስገባት ላይ…',
  success: 'ምርመራ ተልኳል። ይህን ፓስ በጤና ፍተሻ ያሳዩ።',
  success_cleared: '✅ የኢቦላ የጉዞ ጤና ምርመራዎ ተጠናቋል እና ለጉዞ ተፈቅዷል።\n\nለ21 ቀናት ዕለታዊ የክትትል መልእክቶችን ይጠብቁ።',
  success_high_risk: 'እባክዎ ወደ ወደብ ጤና ዴስክ ይሂዱና QR ኮድ ያሳዩ',
  qr_caption: 'ይህን QR ኮድ በወደብ ጤና ዴስክ ያሳዩ',
  download_pass: 'ፓስ አውርድ (PDF)',
  download_qr: 'QR ኮድ አውርድ',
  downloading: 'በማውረድ ላይ…',
  err_download: 'ማውረድ አልተሳካም። እንደገና ይሞክሩ።',
  another: 'ሌላ ምርመራ',
  err_phone: 'የአገር ኮድ እና ስልክ ቁጥር ያስፈልጋሉ።',
  err_phone_valid: 'ትክክለኛ ኮድ እና ቁጥር ያስገቡ።',
  err_required: 'እባክዎ ሁሉንም አስፈላጊ መስኮች (*) ይሙሉ።',
  err_symptoms: 'ቢያንስ አንድ ምልክት ይምረጡ፣ ወይም “ከላይ ያሉት አይደሉም”።',
  err_save: 'ማስገባት አልተሳካም። እንደገና ይሞክሩ።',
  no_pass: 'ፓስ ገና አልተፈጠረም።',
};

const es: Dict = {
  ...en,
  brand_eyebrow: 'República de Kenia · MoH',
  brand_title: 'Control sanitario del viajero',
  brand_sub: 'Formulario de Ébola — sin inicio de sesión. Temperatura en el control.',
  language: 'Idioma',
  demographics: 'Datos personales',
  travel: 'Detalles del viaje',
  exposure: 'Historial / Exposición',
  symptoms: 'Síntomas *',
  symptoms_hint: 'Seleccione todo lo que corresponda',
  full_name: 'Nombre completo *',
  age: 'Edad *',
  gender: 'Género *',
  male: 'Masculino',
  female: 'Femenino',
  other: 'Otro',
  nationality: 'Nacionalidad *',
  passport: 'Pasaporte / N.º de identidad *',
  country_code: 'Código de país *',
  country_code_hint: 'p. ej. +254',
  phone: 'Número de teléfono *',
  phone_hint: 'Número local sin código de país',
  departure: 'País de salida *',
  country_name: 'Nombre del país *',
  mode: 'Medio de transporte *',
  air: 'Aire',
  ship: 'Mar',
  road: 'Carretera',
  poe: 'Punto de entrada *',
  poe_loading: 'Cargando puntos de entrada…',
  poe_need_mode: 'Seleccione primero el medio de transporte',
  poe_error: 'No se pudieron cargar los puntos de entrada',
  flight: 'N.º de vuelo *',
  vessel: 'N.º de buque *',
  vehicle: 'N.º de vehículo *',
  seat: 'N.º de asiento *',
  visited_ug_drc: '¿Visitó Uganda o RDC en los últimos 21 días? *',
  yes: 'Sí',
  no: 'No',
  exp_contact: '¿Contacto con un caso de Ébola confirmado o sospechoso?',
  exp_healthcare: '¿Trabajo sanitario o de laboratorio en zona afectada?',
  exp_funeral: '¿Asistió a un funeral o preparación del cuerpo?',
  exp_animals: '¿Contacto con murciélagos, primates o monos?',
  exp_bushmeat: '¿Cazó, faenó o manipuló carne de monte?',
  exp_forest: '¿Exposición a bosques o cuevas?',
  sym_headache: 'Dolor de cabeza',
  sym_muscle: 'Dolor muscular o articular',
  sym_vomit: 'Vómitos o náuseas',
  sym_diarrhoea: 'Diarrea',
  sym_abdominal: 'Dolor abdominal',
  sym_bleeding: 'Sangrado o hematomas',
  sym_rash: 'Erupción hemorrágica o petequias',
  sym_eyes: 'Ojos rojos',
  sym_throat: 'Dolor de garganta',
  sym_swallow: 'Dificultad para tragar',
  sym_consciousness: 'Alteración de la conciencia',
  sym_none: 'Ninguno de los anteriores',
  submit: 'Enviar y obtener pase',
  submitting: 'Enviando…',
  success: 'Control enviado. Muestre este pase en el control sanitario.',
  success_cleared: '✅ Su control sanitario de Ébola para el viaje está completo y está autorizado para viajar.\n\nEspere mensajes de seguimiento diarios durante 21 días.',
  success_high_risk: 'Acuda al mostrador de salud portuaria y muestre el código QR',
  qr_caption: 'Muestre este código QR en el mostrador de salud portuaria',
  download_pass: 'Descargar pase (PDF)',
  download_qr: 'Descargar código QR',
  downloading: 'Descargando…',
  err_download: 'Error al descargar. Inténtelo de nuevo.',
  another: 'Otro control',
  err_phone: 'Se requieren código de país y teléfono.',
  err_phone_valid: 'Introduzca un código y teléfono válidos.',
  err_required: 'Complete todos los campos obligatorios (*).',
  err_symptoms: 'Seleccione al menos un síntoma, o “Ninguno de los anteriores”.',
  err_save: 'Error al enviar. Inténtelo de nuevo.',
  no_pass: 'Aún no se generó el pase.',
};

const ar: Dict = {
  ...en,
  brand_eyebrow: 'جمهورية كينيا · وزارة الصحة',
  brand_title: 'فحص صحة المسافر',
  brand_sub: 'نموذج إيبولا — بدون تسجيل دخول. تُقاس الحرارة عند نقطة التفتيش.',
  language: 'اللغة',
  demographics: 'البيانات الشخصية',
  travel: 'تفاصيل السفر',
  exposure: 'التاريخ / التعرض',
  symptoms: 'الأعراض *',
  symptoms_hint: 'اختر كل ما ينطبق',
  full_name: 'الاسم الكامل *',
  age: 'العمر *',
  gender: 'الجنس *',
  male: 'ذكر',
  female: 'أنثى',
  other: 'آخر',
  nationality: 'الجنسية *',
  passport: 'جواز السفر / رقم الهوية *',
  country_code: 'رمز الدولة *',
  country_code_hint: 'مثال +254',
  phone: 'رقم الهاتف *',
  phone_hint: 'الرقم المحلي بدون رمز الدولة',
  departure: 'بلد المغادرة *',
  country_name: 'اسم البلد *',
  mode: 'وسيلة النقل *',
  air: 'جوي',
  ship: 'بحري',
  road: 'بري',
  poe: 'نقطة الدخول *',
  poe_loading: 'جاري تحميل نقاط الدخول…',
  poe_need_mode: 'اختر وسيلة النقل أولاً',
  poe_error: 'تعذر تحميل نقاط الدخول',
  flight: 'رقم الرحلة *',
  vessel: 'رقم السفينة *',
  vehicle: 'رقم المركبة *',
  seat: 'رقم المقعد *',
  visited_ug_drc: 'هل زرت أوغندا أو جمهورية الكونغو الديمقراطية خلال آخر 21 يوماً؟ *',
  yes: 'نعم',
  no: 'لا',
  exp_contact: 'مخالطة حالة إيبولا مؤكدة أو مشتبهة؟',
  exp_healthcare: 'عمل صحي أو مخبري في منطقة متأثرة؟',
  exp_funeral: 'حضور جنازة أو تجهيز جثمان؟',
  exp_animals: 'مخالطة خفافيش أو رئيسيات أو قرود؟',
  exp_bushmeat: 'صيد أو ذبح أو تداول لحوم الأدغال؟',
  exp_forest: 'التعرض للغابات أو الكهوف؟',
  sym_headache: 'صداع',
  sym_muscle: 'ألم عضلي أو مفصلي',
  sym_vomit: 'قيء أو غثيان',
  sym_diarrhoea: 'إسهال',
  sym_abdominal: 'ألم بطني',
  sym_bleeding: 'نزيف أو كدمات',
  sym_rash: 'طفح نزفي أو نمشات',
  sym_eyes: 'احمرار العينين',
  sym_throat: 'التهاب الحلق',
  sym_swallow: 'صعوبة البلع',
  sym_consciousness: 'تغيّر الوعي',
  sym_none: 'لا شيء مما سبق',
  submit: 'إرسال والحصول على التصريح',
  submitting: 'جاري الإرسال…',
  success: 'تم إرسال الفحص. أظهر هذا التصريح عند نقطة الصحة.',
  success_cleared: '✅ اكتمل فحص صحة السفر لإيبولا وأنت مسموح بالسفر.\n\nيرجى توقع رسائل متابعة يومية لمدة 21 يوماً.',
  success_high_risk: 'يرجى التوجه إلى مكتب صحة الميناء وإظهار رمز QR',
  qr_caption: 'أظهر رمز QR هذا في مكتب صحة الميناء',
  download_pass: 'تحميل التصريح (PDF)',
  download_qr: 'تحميل رمز QR',
  downloading: 'جاري التحميل…',
  err_download: 'فشل التحميل. حاول مرة أخرى.',
  another: 'فحص آخر',
  err_phone: 'رمز الدولة ورقم الهاتف مطلوبان.',
  err_phone_valid: 'أدخل رمزاً ورقماً صالحين.',
  err_required: 'يرجى تعبئة جميع الحقول الإلزامية (*).',
  err_symptoms: 'اختر عرضاً واحداً على الأقل، أو «لا شيء مما سبق».',
  err_save: 'فشل الإرسال. حاول مرة أخرى.',
  no_pass: 'لم يتم إنشاء التصريح بعد.',
};

const TABLES: Record<PublicLang, Dict> = { en, fr, zh, sw, am, es, ar };

export function tPublic(lang: PublicLang, key: string): string {
  return TABLES[lang]?.[key] ?? TABLES.en[key] ?? key;
}

export function isRtl(lang: PublicLang): boolean {
  return lang === 'ar';
}

export const SYMPTOM_I18N_KEYS: Record<string, string> = {
  headache: 'sym_headache',
  muscle_joint_pain: 'sym_muscle',
  vomiting_nausea: 'sym_vomit',
  diarrhoea: 'sym_diarrhoea',
  abdominal_pain: 'sym_abdominal',
  bleeding_bruising: 'sym_bleeding',
  haemorrhagic_rash: 'sym_rash',
  conjunctival: 'sym_eyes',
  sore_throat: 'sym_throat',
  difficulty_swallowing: 'sym_swallow',
  altered_consciousness: 'sym_consciousness',
  none: 'sym_none',
};

export const EXPOSURE_I18N_KEYS: Record<string, string> = {
  ebola_contact_confirmed_case: 'exp_contact',
  ebola_healthcare_lab_work: 'exp_healthcare',
  ebola_funeral_body_prep: 'exp_funeral',
  ebola_contact_animals: 'exp_animals',
  ebola_bushmeat_handling: 'exp_bushmeat',
  ebola_forest_cave_exposure: 'exp_forest',
};
